import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiResponse } from '@app/shared/_models/common';
import { jsonheader } from '@app/shared/constants';

export type PasswordState = 'argon2' | 'legacy' | 'none';

export interface SuperadminEmployee {
  id: number;
  is_active: boolean | null;
  leave_date: string | null;
  roles: number[] | null;
}

// Une ligne de POST /superadmin/users : l'empreinte ne sort jamais de la base, seul son état est calculé en SQL.
export interface SuperadminUserRow {
  id: number;
  store: number | null;
  firstname: string | null;
  lastname: string | null;
  login: string | null;
  email: string | null;
  habilitations: number[] | null;
  password_state: PasswordState;
  employee: SuperadminEmployee | null;
}

// Corps `user` de POST /superadmin/user : null = « pas d'accès » (login), « inchangé » (password), « fiche intouchée » (roles).
export interface SuperadminUserInput {
  id: number | null;
  store: number;
  firstname: string | null;
  lastname: string;
  login: string | null;
  email: string | null;
  habilitations: number[];
  roles: number[] | null;
  password: string | null;
}

export interface SuperadminSaveResult {
  id: number;
  created: boolean;
  password_set: boolean;
  employee_id: number | null;
}

// Refus métier (HTTP 200, success:false), distingué des refus d'identité qui arrivent en vrai statut HTTP.
export class SuperadminRefusal {
  constructor(public readonly code: number, public readonly message: string) {}
}

export interface SuperadminFailure {
  message: string;
  relock: boolean;
}

// Traduction de tout échec des deux routes ; relock = oublier le mot de passe gardé par l'écran.
export function superadminFailureOf(e: unknown): SuperadminFailure {
  if (e instanceof SuperadminRefusal) return { message: e.message, relock: false };
  if (!(e instanceof HttpErrorResponse)) return { message: 'La demande a échoué.', relock: false };
  const body = e.error as { error_msg?: unknown; msg?: unknown } | null;
  const serverMsg = typeof body?.error_msg === 'string' && body.error_msg ? body.error_msg : typeof body?.msg === 'string' && body.msg ? body.msg : '';
  switch (e.status) {
    case 401:
      return { message: 'Session à rouvrir : reconnectez-vous.', relock: true };
    case 403:
      return /mot de passe/i.test(serverMsg) ? { message: 'Mot de passe incorrect.', relock: true } : { message: 'Réservé aux administrateurs.', relock: false };
    case 429:
      return { message: "Trop d'essais : réessayez dans 15 minutes.", relock: false };
    case 400:
      return { message: serverMsg || 'Requête refusée par le serveur.', relock: false };
    case 0:
      // En production, le 401 du middleware sort sans en-têtes CORS : le navigateur n'en montre qu'un statut 0.
      return { message: 'Serveur injoignable ou session refusée : vérifiez la connexion, puis reconnectez-vous si besoin.', relock: false };
    default:
      return { message: 'Erreur du serveur' + (e.status ? ' (HTTP ' + e.status + ')' : '') + '.', relock: false };
  }
}

// Ne garde ni ne journalise aucun mot de passe : le composant le détient et le passe à chaque appel.
@Injectable({ providedIn: 'root' })
export class UserManagementService {
  private http = inject(HttpClient);

  listUsers$(currentPassword: string): Observable<SuperadminUserRow[]> {
    return this.http.post<ApiResponse<{ users?: SuperadminUserRow[] }>>(environment.server_url + '/superadmin/users', { current_password: currentPassword }, { headers: jsonheader }).pipe(map(res => unwrap(res).users ?? []));
  }

  saveUser$(currentPassword: string, user: SuperadminUserInput): Observable<SuperadminSaveResult> {
    return this.http.post<ApiResponse<SuperadminSaveResult>>(environment.server_url + '/superadmin/user', { current_password: currentPassword, user }, { headers: jsonheader }).pipe(map(res => unwrap(res)));
  }
}

function unwrap<T>(res: ApiResponse<T> | null | undefined): T {
  if (!res?.success || res.data == null) throw new SuperadminRefusal(res?.error_code ?? 0, res?.error_msg || res?.msg || 'Refusé par le serveur.');
  return res.data;
}
